import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import {
	checkStringEquality,
	DcDxGrids,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from 'src/app/core/dx-types';

import {
	DxListsStorage,
	IDxList,
} from '../../../core/services/dx-lists.storage';
import { goToHdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { GdIcons } from '../../../ui/app-gd.icons';
import { HdIcons } from '../../../ui/app-hd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	duplicateGrid,
	GridSelector,
	initGridList,
	navigateToGridCreate,
	navigateToGridEdit,
	openDeleteConfirmationPopup,
	refreshGridList,
	toggleGridStatus,
} from '../../store';
import { GridItemEntity } from './grid-item.entity';
import { GridListEntity } from './grid-list.entity';

export type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

@Component({
	selector: 'app-grid-list',
	templateUrl: './grid-list.component.html',
	styleUrls: ['./grid-list.component.scss'],
	inputs: ['projectLabel'],
})
export class GridListComponent extends DcBaseComponent implements OnInit {
	protected readonly DcIcons = DcIcons;
	protected readonly HdIcons = HdIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public paths: Array<IBreadCrumbEntry> = [];
	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const grid = item as GridItemEntity;
				this.toggleActivationStatus(grid);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];
	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const grid = item as GridItemEntity;
				this.toggleActivationStatus(grid);
			},
			icon: ActionIcons.ActivateElement,
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public vo$!: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: GridListEntity;
	}>;
	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};
	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;
	private readonly gridListSubject = new ReplaySubject<GridListEntity>(1);

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	protected readonly GdIcons = GdIcons;
	public constructor(
		private readonly store: Store,
		private readonly gridSelector: GridSelector,
		private readonly dxStorage: DxListsStorage,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'grid-list';
		this.dxLocalStorageKey = DcDxGrids.GridListStandalone;

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.gridSelector.getLoading$(),
			list: this.gridListSubject,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.triggerInit();
		this.listenToEvents();
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshGridList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public createNewGrid(): void {
		this.store.dispatch(navigateToGridCreate());
	}

	public navigateToGrid(grid: GridItemEntity, goToVisuTab = false): void {
		this.store.dispatch(
			navigateToGridEdit({
				id: grid.id,
				gridType: grid.type,
				goToVisuTab,
			})
		);
	}

	public deleteGrid(event: Event, grid: GridItemEntity): void {
		event.stopPropagation();
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				gridId: [grid.id],
				gridLabel: grid.label,
				gridType: [grid.type],
			})
		);
	}

	public toggleActivationStatus(grid: GridItemEntity): void {
		this.store.dispatch(
			toggleGridStatus({
				gridId: grid.id,
				gridType: grid.type,
			})
		);
	}

	public duplicateGrid(event: Event, grid: GridItemEntity): void {
		event.stopPropagation();
		this.store.dispatch(
			duplicateGrid({
				gridId: grid.id,
				gridType: grid.type,
			})
		);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: GridItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();
			if (tags.length === 0 && value === NoopValue) {
				return true;
			}

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: GridItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private triggerInit(): void {
		this.store.dispatch(initGridList());

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToHdHome()),
						},
						{
							label: $localize`:i18n=@@grid.list.title:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.gridSelector
			.getGridList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();

					this.gridListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();
	}

	private listenToEvents(): void {
		this.gridSelector
			.getSuccessDeleteGrids$()
			.pipe(
				takeUntil(this.onDestroy$),
				tap((success) => {
					if (success) {
						this.grid?.instance.clearSelection();
						this.grid?.instance.deselectAll();
					}
				})
			)
			.subscribe();
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedGrids = await firstValueFrom(
			this.gridSelector.getSelectedGrids$(selectedRowKeys)
		);
		if (selectedGrids.length === 1) {
			return this.store.dispatch(
				openDeleteConfirmationPopup({
					gridId: [selectedGrids[0].id],
					gridLabel: selectedGrids[0].label,
					gridType: [selectedGrids[0].type],
				})
			);
		}
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				gridId: selectedGrids.map((k) => k.id),
				gridLabel: '',
				gridType: selectedGrids.map((k) => k.type),
			})
		);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@grid.list.columnSelection:`,
				typed: '',
			},
			{
				order: 1,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@grid.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
